/**
 * Buyer Hub send authority — marks a corporate Hub-sent and dispatches invites.
 */

import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/services/logger";
import type {
  BuyerHubSentRecord,
  MarkBuyerHubSentParams,
  SendBuyerHubInviteParams,
} from "@/types/BuyerHub";

type BuyerHubSentRow = {
  corporate_id: number;
  issuer_corporate_id: number | null;
  program_id: string | null;
  sent_at: string;
};

function buyerHubSentTable() {
  return supabase.from("corporate_buyer_hub_sent");
}

function mapSentRow(row: BuyerHubSentRow): BuyerHubSentRecord {
  return {
    corporateId: row.corporate_id,
    issuerCorporateId: row.issuer_corporate_id,
    programId: row.program_id,
    sentAt: row.sent_at,
  };
}

export const hubSendService = {
  async getSentForCorporate(corporateId: number): Promise<BuyerHubSentRecord | null> {
    const { data, error } = await buyerHubSentTable()
      .select("corporate_id, issuer_corporate_id, program_id, sent_at")
      .eq("corporate_id", corporateId)
      .is("revoked_at", null)
      .maybeSingle();

    if (error) {
      logger.error("Failed to load Buyer Hub sent record", error, { corporateId });
      throw error;
    }

    return data ? mapSentRow(data) : null;
  },

  async listActiveSentForCorporates(corporateIds: number[]): Promise<BuyerHubSentRecord[]> {
    if (corporateIds.length === 0) return [];

    const { data, error } = await buyerHubSentTable()
      .select("corporate_id, issuer_corporate_id, program_id, sent_at")
      .in("corporate_id", corporateIds)
      .is("revoked_at", null)
      .order("corporate_id", { ascending: true });

    if (error) {
      logger.error("Failed to list Buyer Hub sent records", error, { corporateIds });
      throw error;
    }

    return (data ?? []).map(mapSentRow);
  },

  async markSent(params: MarkBuyerHubSentParams): Promise<BuyerHubSentRecord> {
    const { data, error } = await supabase.rpc("mark_buyer_hub_sent", {
      p_corporate_id: params.corporateId,
      ...(params.issuerCorporateId != null
        ? { p_issuer_corporate_id: params.issuerCorporateId }
        : {}),
      ...(params.programId != null ? { p_program_id: params.programId } : {}),
    });

    if (error) {
      logger.error("Failed to mark Buyer Hub sent", error, { ...params });
      throw error;
    }

    if (!data) {
      throw new Error("mark_buyer_hub_sent returned no row");
    }

    return mapSentRow(data);
  },

  async sendBuyerHubInvite(params: SendBuyerHubInviteParams): Promise<void> {
    await this.markSent({
      corporateId: params.corporateId,
      issuerCorporateId: params.issuerCorporateId ?? null,
      programId: params.programId ?? null,
    });

    const { data, error } = await supabase.functions.invoke("send-invite", {
      body: {
        email: params.email,
        corporate_id: params.corporateId,
        access_level: "owner",
        global_role: "user",
        invite_type: "buyer_hub",
        first_name: params.firstName ?? undefined,
        last_name: params.lastName ?? undefined,
        metadata: {
          ...(params.issuerCorporateId ? { issuer_corporate_id: params.issuerCorporateId } : {}),
          ...(params.programId ? { program_id: params.programId } : {}),
        },
      },
    });

    if (error) {
      logger.error("Failed to send Buyer Hub invite", error, { email: params.email });
      throw new Error(error.message ?? "Failed to send invite");
    }

    if (data?.error) {
      throw new Error(data.error);
    }
  },
};

/** Stable path for a Hub-sent corporate home surface. */
export function buyerHubHomePath(corporateId: number): string {
  return `/buyer-hub/${corporateId}`;
}

/** Preserve operator preview mode across Hub in-app navigation (U8). */
export function appendBuyerHubPreviewQuery(path: string): string {
  if (path.includes("preview=1")) return path;
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}preview=1`;
}

/** Routes Hub-sent A1 users may reach outside the Hub tree. */
export function isHubBuyerAllowedPath(pathname: string): boolean {
  if (pathname.startsWith("/buyer-hub/")) return true;
  if (pathname.startsWith("/auth")) return true;
  if (pathname === "/post-auth") return true;
  if (pathname.startsWith("/reset-password")) return true;
  if (pathname.startsWith("/complete-profile/")) return true;
  return false;
}
